import { memo, useId } from 'react';
import type { ReactNode } from 'react';

/**
 * The everyday things IN ORDER shows in five stages each, drawn in a 100 × 100 box: a seed
 * growing into a tree, a candle burning down, a glass being filled, a ship being built, an apple
 * being eaten, an egg hatching, a snowman being built, a house going up. Each runs one way only
 * (a candle never grows back), so the order is never a matter of opinion.
 */

export type SeqId = 'grow' | 'candle' | 'pour' | 'ship' | 'apple' | 'hatch' | 'snow' | 'build';

export const SEQ_IDS: readonly SeqId[] = ['grow', 'candle', 'pour', 'ship', 'apple', 'hatch', 'snow', 'build'];

export const SEQ_NAMES: Record<SeqId, readonly [string, string]> = {
  grow: ['a seed grows', 'et frø vokser'],
  candle: ['a candle burns', 'et lys brenner'],
  pour: ['a glass is filled', 'et glass fylles'],
  ship: ['a ship is built', 'et skip bygges'],
  apple: ['an apple is eaten', 'et eple spises'],
  hatch: ['an egg hatches', 'et egg klekkes'],
  snow: ['a snowman is made', 'en snømann lages'],
  build: ['a house is built', 'et hus bygges'],
};

/** Every sequence has five stages, 0 to 4. */
export const STAGES = 5;

type Draw = (k: number, p: string) => ReactNode;

const INK = '#2a2118';

const grow: Draw = (k) => {
  const stem = [0, 14, 30, 44, 50][k]!;
  return (
    <>
      <rect x="0" y="80" width="100" height="20" fill="#6b4a2a" />
      <path d="M0 80H100" stroke="#8a6a44" strokeWidth="2" />
      <ellipse cx="50" cy="90" rx="5" ry="3.6" fill="#d9b070" stroke={INK} strokeWidth=".8" />
      {k >= 1 && <path d="M50 90C48 94 46 96 44 98M50 90C52 94 55 95 57 98" stroke="#e8d6b0" strokeWidth="1" fill="none" />}
      {k >= 1 && k <= 2 && <path d={`M50 88V${80 - stem}`} stroke="#4f9a3a" strokeWidth={k === 1 ? 2 : 2.6} />}
      {k === 1 && (
        <>
          <path d="M50 67C44 62 40 64 38 66C42 70 46 70 50 67Z" fill="#6cc85a" stroke={INK} strokeWidth=".6" />
          <path d="M50 67C56 62 60 64 62 66C58 70 54 70 50 67Z" fill="#6cc85a" stroke={INK} strokeWidth=".6" />
        </>
      )}
      {k === 2 && (
        <>
          <path d="M50 52C42 44 36 46 33 49C38 55 45 55 50 52Z" fill="#6cc85a" stroke={INK} strokeWidth=".6" />
          <path d="M50 52C58 44 64 46 67 49C62 55 55 55 50 52Z" fill="#6cc85a" stroke={INK} strokeWidth=".6" />
          <path d="M50 66C43 60 38 62 36 65C40 70 46 70 50 66Z" fill="#5fb44c" stroke={INK} strokeWidth=".6" />
          <path d="M50 66C57 60 62 62 64 65C60 70 54 70 50 66Z" fill="#5fb44c" stroke={INK} strokeWidth=".6" />
        </>
      )}
      {k === 3 && (
        <>
          <path d="M48.5 80V40H51.5V80Z" fill="#7a4a22" />
          <circle cx="50" cy="34" r="15" fill="#4fa83e" stroke={INK} strokeWidth=".8" />
          <circle cx="45" cy="29" r="5" fill="#7cd064" />
        </>
      )}
      {k === 4 && (
        <>
          <path d="M45 80C47 66 47 56 46 46H54C53 56 53 66 55 80Z" fill="#7a4a22" stroke={INK} strokeWidth=".8" />
          <g fill="#3f9a3a" stroke={INK} strokeWidth=".8">
            <circle cx="34" cy="38" r="15" />
            <circle cx="66" cy="38" r="15" />
            <circle cx="50" cy="24" r="18" />
            <circle cx="50" cy="44" r="14" />
          </g>
          <circle cx="42" cy="18" r="6" fill="#6cc85a" />
          {[[34, 36], [62, 30], [52, 44], [70, 44], [44, 26]].map(([x, y]) => (
            <circle key={`${x}`} cx={x} cy={y} r="3.2" fill="#e8473c" stroke={INK} strokeWidth=".6" />
          ))}
        </>
      )}
    </>
  );
};

const candle: Draw = (k) => {
  const h = [58, 58, 40, 22, 9][k]!;
  const top = 84 - h;
  return (
    <>
      <ellipse cx="50" cy="86" rx="26" ry="5" fill="#b58a3a" stroke={INK} strokeWidth=".8" />
      <path d="M26 86V89Q50 96 74 89V86" fill="#8a6428" />
      <rect x="40" y={top} width="20" height={h} fill="#f3ead6" stroke={INK} strokeWidth=".8" />
      <rect x="43" y={top + 2} width="4" height={Math.max(0, h - 4)} fill="#ffffff" opacity=".6" />
      {k >= 2 && <path d={`M40 ${top}V${top + 7}Q42 ${top + 9} 43 ${top + 6}V${top}Z M56 ${top}V${top + 11}Q58 ${top + 13} 59 ${top + 10}V${top}Z`} fill="#fff8e8" stroke={INK} strokeWidth=".6" />}
      {k >= 3 && <path d="M34 86Q36 80 40 82M60 82Q64 80 66 86" stroke="#f3ead6" strokeWidth="3" fill="none" />}
      <path d={`M50 ${top}V${top - 5}`} stroke={INK} strokeWidth="1.4" />
      {k >= 1 && k <= 3 && (
        <>
          <circle cx="50" cy={top - 12} r="13" fill="#ffd15a" opacity=".22" />
          <path d={`M50 ${top - 22}C44 ${top - 14} 45 ${top - 6} 50 ${top - 4}C55 ${top - 6} 56 ${top - 14} 50 ${top - 22}Z`} fill="#ffc24a" stroke="#e07a2a" strokeWidth=".8" />
          <path d={`M50 ${top - 14}C48 ${top - 10} 48 ${top - 7} 50 ${top - 6}C52 ${top - 7} 52 ${top - 10} 50 ${top - 14}Z`} fill="#fff8d0" />
        </>
      )}
      {k === 4 && <path d={`M50 ${top - 6}C44 ${top - 14} 56 ${top - 20} 50 ${top - 28}C45 ${top - 34} 54 ${top - 40} 50 ${top - 46}`} stroke="#9aa7b0" strokeWidth="2" fill="none" strokeLinecap="round" />}
    </>
  );
};

const pour: Draw = (k, p) => {
  const level = [0, 0.3, 0.55, 0.8, 0.97][k]!;
  const y = 88 - level * 50;
  const tilted = k >= 1 && k <= 3;
  return (
    <>
      <defs>
        <clipPath id={`${p}g`}>
          <path d="M30 36H64L60 88H34Z" />
        </clipPath>
      </defs>
      <rect x="0" y="88" width="100" height="12" fill="#8a6a44" />
      {level > 0 && (
        <g clipPath={`url(#${p}g)`}>
          <rect x="20" y={y} width="60" height="60" fill="#e8a33a" />
          <rect x="20" y={y} width="60" height="3" fill="#ffe0a0" />
          {k === 4 && <rect x="20" y={y - 4} width="60" height="5" fill="#fff8e8" />}
        </g>
      )}
      <path d="M30 36H64L60 88H34Z" fill="#dff4ff" fillOpacity=".18" stroke="#6a8aa0" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M34 40L37 84" stroke="#ffffff" strokeWidth="2" opacity=".6" />
      {tilted ? (
        <>
          <path d={`M48 ${16}C48 30 47 ${y - 6} 47 ${y}`} stroke="#e8a33a" strokeWidth="3" fill="none" />
          <g transform="rotate(-58 70 20)">
            <path d="M60 6H82V34Q82 38 78 38H64Q60 38 60 34Z" fill="#3f86e8" stroke={INK} strokeWidth=".9" />
            <path d="M82 12C92 12 92 28 82 28" stroke="#3f86e8" strokeWidth="3.4" fill="none" />
            <path d="M60 6L55 2L60 12Z" fill="#3f86e8" stroke={INK} strokeWidth=".8" />
          </g>
        </>
      ) : (
        <g>
          <path d="M70 50H90V84Q90 88 86 88H74Q70 88 70 84Z" fill="#3f86e8" stroke={INK} strokeWidth=".9" />
          <path d="M90 56C99 56 99 72 90 72" stroke="#3f86e8" strokeWidth="3.4" fill="none" />
          <path d="M70 50L65 45L70 55Z" fill="#3f86e8" stroke={INK} strokeWidth=".8" />
        </g>
      )}
    </>
  );
};

const ship: Draw = (k) => (
  <>
    {k < 4 ? (
      <>
        <rect x="0" y="84" width="100" height="16" fill="#8a8f98" />
        <path d="M24 84V78H30V84ZM48 84V78H54V84ZM70 84V78H76V84Z" fill="#6b4a2a" />
      </>
    ) : (
      <>
        <rect x="0" y="80" width="100" height="20" fill="#3f86e8" />
        <path d="M6 88q4-3 8 0t8 0M60 92q4-3 8 0t8 0M36 96q4-3 8 0" stroke="#9fd2ff" strokeWidth="1.2" fill="none" />
      </>
    )}
    {k === 0 && <path d="M16 74H84V78H16ZM16 78L12 66H16ZM84 78L90 64H86Z" fill="#a0692f" stroke={INK} strokeWidth=".8" />}
    {k === 1 && (
      <>
        <path d="M16 74H84V78H16Z" fill="#a0692f" stroke={INK} strokeWidth=".8" />
        <path d="M22 76C20 68 20 62 22 58M32 76C30 66 30 60 32 56M42 76C40 66 40 58 42 54M52 76C50 66 50 58 52 54M62 76C60 66 60 58 62 54M72 76C70 66 70 60 72 56M80 76C80 68 81 62 84 58" stroke="#a0692f" strokeWidth="2.4" fill="none" />
        <path d="M14 76L10 58M86 76L92 56" stroke="#a0692f" strokeWidth="2.4" />
      </>
    )}
    {k >= 2 && (
      <>
        <path d={`M10 56H92L84 ${k === 4 ? 82 : 78}H18Z`} fill="#8a5427" stroke={INK} strokeWidth=".9" />
        <path d="M13 62H90M15 68H88M17 74H86" stroke="#6b3a1f" strokeWidth="1" />
        <path d="M24 60h2M40 60h2M56 60h2M72 60h2" stroke={INK} strokeWidth="2" />
      </>
    )}
    {k >= 3 && <path d="M36 56V14M64 56V20" stroke="#5a3a1a" strokeWidth="2.4" />}
    {k === 4 && (
      <>
        <path d="M24 18Q36 16 48 18L47 50Q36 48 25 50Z" fill="#f4ead2" stroke={INK} strokeWidth=".8" />
        <path d="M54 24Q64 22 74 24L73 52Q64 50 55 52Z" fill="#f4ead2" stroke={INK} strokeWidth=".8" />
        <path d="M36 14L46 16.5L36 19Z" fill="#e8473c" />
      </>
    )}
  </>
);

const apple: Draw = (k, p) => {
  // Bites taken so far: each is a circle cut from the side.
  const bites: [number, number, number][] = [
    [],
    [[82, 50, 13]],
    [[82, 50, 13], [18, 58, 13]],
    [[80, 46, 20], [20, 56, 19]],
    [],
  ][k] as [number, number, number][];
  if (k === 4) {
    return (
      <>
        <ellipse cx="50" cy="90" rx="22" ry="3" fill="rgba(0,0,0,.18)" />
        <path d="M50 22C50 16 52 12 56 8" stroke="#6b4a2a" strokeWidth="2.6" fill="none" strokeLinecap="round" />
        <path d="M36 26C42 20 58 20 64 26C60 30 40 30 36 26Z" fill="#e8473c" stroke={INK} strokeWidth=".8" />
        <path d="M40 28C46 40 46 62 40 76H60C54 62 54 40 60 28Z" fill="#f5ecc4" stroke={INK} strokeWidth=".8" />
        <path d="M36 80C42 74 58 74 64 80C58 88 42 88 36 80Z" fill="#e8473c" stroke={INK} strokeWidth=".8" />
        <ellipse cx="47" cy="48" rx="2" ry="3.4" fill="#5a3a1a" />
        <ellipse cx="53" cy="56" rx="2" ry="3.4" fill="#5a3a1a" />
      </>
    );
  }
  const body = 'M50 28C58 20 80 20 82 44C84 68 66 88 56 86C52 85 48 85 44 86C34 88 16 68 18 44C20 20 42 20 50 28Z';
  return (
    <>
      <defs>
        <mask id={`${p}f`}>
          <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
          {bites.map(([x, y, r]) => (
            <circle key={x} cx={x} cy={y} r={r} fill="#000000" />
          ))}
        </mask>
        <mask id={`${p}s`}>
          <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
          {bites.map(([x, y, r]) => (
            <circle key={x} cx={x} cy={y} r={r + 3} fill="#000000" />
          ))}
        </mask>
      </defs>
      <ellipse cx="50" cy="90" rx="26" ry="3" fill="rgba(0,0,0,.18)" />
      <path d="M50 28C50 20 52 14 56 9" stroke="#6b4a2a" strokeWidth="2.6" fill="none" strokeLinecap="round" />
      <path d="M55 16C61 7 71 7 76 11C70 20 61 20 55 16Z" fill="#6cc85a" stroke={INK} strokeWidth=".7" />
      <path d={body} fill="#f5ecc4" stroke={INK} strokeWidth=".8" mask={`url(#${p}f)`} />
      <path d={body} fill="#e8473c" stroke={INK} strokeWidth=".8" mask={`url(#${p}s)`} />
      <path d="M28 40C30 32 36 28 42 28" stroke="#ffffff" strokeWidth="3" fill="none" opacity=".5" strokeLinecap="round" mask={`url(#${p}s)`} />
    </>
  );
};

const hatch: Draw = (k) => (
  <>
    <ellipse cx="50" cy="86" rx="36" ry="9" fill="#c9a46a" stroke={INK} strokeWidth=".8" />
    <path d="M18 84Q30 80 40 86M44 82Q56 78 66 84M62 86Q72 82 82 86" stroke="#8a6a3a" strokeWidth="1.2" fill="none" />
    {k <= 2 && (
      <>
        <path d="M50 24C64 24 72 46 72 62C72 76 62 84 50 84C38 84 28 76 28 62C28 46 36 24 50 24Z" fill="#f6efe0" stroke={INK} strokeWidth=".9" />
        <path d="M38 40C40 34 44 30 48 29" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" />
      </>
    )}
    {k === 1 && <path d="M40 50L45 46L49 52L53 47" stroke={INK} strokeWidth="1.3" fill="none" />}
    {k === 2 && (
      <>
        <path d="M30 52L36 46L41 53L46 45L51 53L56 45L61 52L66 46L70 52" stroke={INK} strokeWidth="1.4" fill="none" />
        <path d="M46 45L51 53L56 45L52 40Z" fill="#2a2118" />
        <path d="M49 48l2 3" stroke="#f2c230" strokeWidth="2" />
      </>
    )}
    {k === 3 && (
      <>
        <circle cx="50" cy="44" r="13" fill="#f7d44a" stroke={INK} strokeWidth=".8" />
        <circle cx="46" cy="41" r="1.8" fill={INK} />
        <circle cx="55" cy="41" r="1.8" fill={INK} />
        <path d="M48 46L51 50L54 46Z" fill="#f08a2c" />
        <path d="M28 62C28 58 29 54 30 52L36 46L41 53L46 45L51 53L56 45L61 52L66 46L70 52C71 54 72 58 72 62C72 76 62 84 50 84C38 84 28 76 28 62Z" fill="#f6efe0" stroke={INK} strokeWidth=".9" />
      </>
    )}
    {k === 4 && (
      <>
        <path d="M14 78C14 70 18 66 22 66L25 70L28 66L31 70L34 66C36 68 36 74 36 78C36 84 30 86 25 86C19 86 14 84 14 78Z" fill="#f6efe0" stroke={INK} strokeWidth=".8" />
        <path d="M66 78C66 72 70 68 74 68L77 72L80 68L83 72L86 68C87 70 88 74 88 78C88 84 82 86 77 86C71 86 66 84 66 78Z" fill="#f6efe0" stroke={INK} strokeWidth=".8" />
        <ellipse cx="50" cy="66" rx="14" ry="13" fill="#f7d44a" stroke={INK} strokeWidth=".8" />
        <circle cx="52" cy="44" r="11" fill="#f7d44a" stroke={INK} strokeWidth=".8" />
        <circle cx="55" cy="41" r="1.8" fill={INK} />
        <path d="M61 44L67 46L61 48Z" fill="#f08a2c" />
        <path d="M40 64C44 58 50 60 50 66" stroke="#e0b830" strokeWidth="2" fill="none" />
        <path d="M46 78V84M54 78V84" stroke="#f08a2c" strokeWidth="2" />
      </>
    )}
  </>
);

const snow: Draw = (k) => (
  <>
    <path d="M0 82Q50 76 100 82V100H0Z" fill="#eef4fa" stroke="#b8c8d8" strokeWidth="1" />
    {k === 0 && <circle cx="50" cy="74" r="8" fill="#ffffff" stroke="#9fb0c2" strokeWidth="1" />}
    {k >= 1 && <circle cx="50" cy="64" r="18" fill="#ffffff" stroke="#9fb0c2" strokeWidth="1" />}
    {k >= 2 && <circle cx="50" cy="36" r="12.5" fill="#ffffff" stroke="#9fb0c2" strokeWidth="1" />}
    {k >= 3 && <circle cx="50" cy="16" r="8.5" fill="#ffffff" stroke="#9fb0c2" strokeWidth="1" />}
    {k === 4 && (
      <>
        <path d="M41 9H59V11H55V2H45V11H41Z" fill="#26303a" />
        <circle cx="47" cy="15" r="1.3" fill={INK} />
        <circle cx="53" cy="15" r="1.3" fill={INK} />
        <path d="M50 18L60 20L50 21Z" fill="#f08a2c" />
        <path d="M40 26H60V30H40ZM54 30H59V40H54Z" fill="#e8473c" />
        <path d="M38 36L24 26M62 36L76 24M24 26L20 22M24 26L19 28M76 24L80 19M76 24L81 26" stroke="#6b4a2a" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="50" cy="36" r="1.4" fill={INK} />
        <circle cx="50" cy="42" r="1.4" fill={INK} />
        <circle cx="50" cy="58" r="1.6" fill={INK} />
      </>
    )}
  </>
);

const build: Draw = (k) => (
  <>
    <rect x="0" y="84" width="100" height="16" fill="#5fae42" />
    <path d="M14 80H86V86H14Z" fill="#9aa2aa" stroke={INK} strokeWidth=".8" />
    {k === 0 && <path d="M20 80V76M40 80V76M60 80V76M80 80V76" stroke="#6b7078" strokeWidth="2" />}
    {k >= 1 && (
      <>
        <path d="M20 44H80V80H20Z" fill={k >= 3 ? '#f3e2c0' : '#d9b88a'} stroke={INK} strokeWidth=".9" />
        {k < 3 && <path d="M20 52H80M20 60H80M20 68H80M20 76H80M30 44V52M50 44V52M70 44V52M40 52V60M60 52V60M30 60V68M50 60V68M70 60V68M40 68V76M60 68V76" stroke="#a8845a" strokeWidth=".7" />}
        <path d={k >= 3 ? 'M30 58H44V80H30Z' : 'M30 58H44V80H30Z'} fill={k >= 3 ? '#7a4a22' : '#3a2a1a'} />
        <path d="M54 54H72V68H54Z" fill={k >= 3 ? '#9fd2ee' : '#3a2a1a'} stroke={k >= 3 ? INK : 'none'} strokeWidth=".8" />
        {k >= 3 && <path d="M63 54V68M54 61H72" stroke="#f3e2c0" strokeWidth="1.2" />}
      </>
    )}
    {k >= 2 && <path d="M14 46L50 18L86 46Z" fill="#c8453c" stroke={INK} strokeWidth=".9" />}
    {k >= 3 && <path d="M66 22H73V36L66 31Z" fill="#7a3a2a" stroke={INK} strokeWidth=".8" />}
    {k === 4 && (
      <>
        <circle cx="72" cy="15" r="3.4" fill="#e6ecf2" />
        <circle cx="76" cy="8" r="4.4" fill="#e6ecf2" opacity=".85" />
        <circle cx="82" cy="2" r="5" fill="#e6ecf2" opacity=".7" />
        <path d="M91 84V62" stroke="#7a4a22" strokeWidth="3" />
        <circle cx="91" cy="56" r="8" fill="#3f9a3a" />
        {[[10, 90, '#e8473c'], [18, 94, '#f2c230'], [60, 92, '#e060a8'], [70, 95, '#ffffff']].map(([x, y, c]) => (
          <circle key={`${x}`} cx={x as number} cy={y as number} r="2.4" fill={c as string} />
        ))}
      </>
    )}
  </>
);

const DRAW: Record<SeqId, Draw> = { grow, candle, pour, ship, apple, hatch, snow, build };

/** One stage of a sequence, as its own SVG: on a page, or nested in a board's SVG at x, y, size (board units). */
export const StagePicture = memo(function StagePicture({ seq, stage, x, y, size }: { seq: SeqId; stage: number; x?: number; y?: number; size?: number }) {
  const p = `o${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <svg viewBox="0 0 100 100" x={x} y={y} width={size ?? '100%'} height={size ?? '100%'} style={{ display: 'block' }} aria-hidden="true">
      {DRAW[seq](stage, p)}
    </svg>
  );
});
